import { Sentry } from "./sentry"

const MAX_RELOADS = 1

/**
 * Report the error to Sentry, then do a full page reload to recover —
 * a fresh server response will bring a consistent layoutConfig + asset bundle.
 *
 * Guards against an infinite loop with a per-boundary sessionStorage key:
 * if we've already reloaded MAX_RELOADS times for this boundary and still
 * crash, we stop reloading and return false so the caller can render a
 * fallback instead. The counter is cleared by clearReloadGuard() once the
 * component mounts successfully.
 *
 * Each boundary passes its own reloadKey so that a header crash doesn't
 * exhaust the footer's reload budget (or vice-versa) when both throw on
 * the same render cycle.
 */
export function reportAndReload(error, info, pageContext, reloadKey) {
  const { layoutConfig, url, component, version } = pageContext

  Sentry.withScope(scope => {
    scope.setTag("location", "InertiaLayout")
    scope.setContext("inertia_page", { url, component, version })
    scope.setContext("layout_config", layoutConfigForSentry(layoutConfig))
    scope.setContext("navigation", navigationContextForSentry())
    scope.setContext("react_error_info", {
      component_stack: info?.componentStack
    })
    scope.setContext("custom", { bypassFilters: true })
    Sentry.captureException(error)
  })

  const reloadCount = parseInt(sessionStorage.getItem(reloadKey) ?? "0", 10)

  if (reloadCount < MAX_RELOADS) {
    sessionStorage.setItem(reloadKey, String(reloadCount + 1))
    window.location.reload()
    return true
  }

  return false
}

/**
 * Clear the reload guard for a boundary after a successful render.
 * Call this from a useEffect in the functional wrapper component.
 */
export function clearReloadGuard(reloadKey) {
  sessionStorage.removeItem(reloadKey)
}

/**
 * Navigation context for Sentry: answers "where did the user come from, and
 * was this the first page they opened in this tab?"
 *
 * - navigation_type: "navigate" | "reload" | "back_forward" | "prerender"
 *   "navigate" on the very first load means the header crashed immediately on
 *   mount — the crash came from the initial server response, not client-side nav.
 * - is_first_page_in_tab: true when type is "navigate" AND referrer is empty.
 */
export function navigationContextForSentry() {
  const navEntry = performance.getEntriesByType("navigation")[0]
  const navigationType = navEntry?.type ?? "unknown"
  const referrer = document.referrer

  return {
    navigation_type: navigationType,
    referrer: referrer || null,
    is_first_page_in_tab: navigationType === "navigate" && referrer === ""
  }
}

/**
 * Snapshot of Inertia `layoutConfig` for Sentry: round-trip through JSON so we
 * only send plain serializable data (matches what came over the wire).
 */
export function layoutConfigForSentry(layoutConfig) {
  if (layoutConfig == null) {
    return { present: false }
  }

  try {
    const json = JSON.stringify(layoutConfig)
    return {
      present: true,
      json,
      snapshot: JSON.parse(json)
    }
  } catch (e) {
    return {
      present: true,
      snapshot_unavailable: true,
      error: String(e?.message ?? e)
    }
  }
}
